import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Spinner, Badge } from 'react-bootstrap';
import { Star, Pencil, Trash2, Plus, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { showSuccess, showError, showConfirm } from '../../../utils/notifications';
import feedbackService, { Feedback, FeedbackCreateRequest } from '../../../services/feedbackService';
import { useUserData } from '../../../hooks';

const AVATAR_COLORS = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Orange
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#14B8A6', // Teal
    '#F97316', // Orange
];

interface SortableFeedbackItemProps {
    feedback: Feedback;
    onEdit: (feedback: Feedback) => void;
    onDelete: (id: number) => void;
    canEdit: boolean;
    canDelete: boolean;
}

const SortableFeedbackItem: React.FC<SortableFeedbackItemProps> = ({
    feedback,
    onEdit,
    onDelete,
    canEdit,
    canDelete,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: feedback.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const renderStars = (rating: number) => {
        return Array.from({ length: 5 }, (_, index) => (
            <Star
                key={index}
                size={16}
                fill={index < rating ? '#F59E0B' : 'none'}
                stroke={index < rating ? '#F59E0B' : '#D1D5DB'}
            />
        ));
    };

    return (
        <div ref={setNodeRef} style={style}>
            <Card className="mb-3 border-0 shadow-sm">
                <Card.Body>
                    <div className="d-flex align-items-start gap-3">
                        {/* Drag Handle */}
                        {canEdit && (
                            <div
                                {...attributes}
                                {...listeners}
                                style={{ cursor: 'grab', paddingTop: '8px' }}
                            >
                                <GripVertical size={20} className="text-muted" />
                            </div>
                        )}

                        {/* Avatar */}
                        <div
                            className="d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: feedback.avatar_color,
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '1rem',
                            }}
                        >
                            {getInitials(feedback.name)}
                        </div>

                        {/* Content */}
                        <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                    <h6 className="mb-1">{feedback.name}</h6>
                                    <div className="d-flex gap-1 mb-2">
                                        {renderStars(feedback.rating)}
                                    </div>
                                </div>
                                <Badge bg="secondary" className="ms-2">
                                    #{feedback.order_position}
                                </Badge>
                            </div>
                            <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                                {feedback.message}
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="d-flex gap-2 flex-shrink-0">
                            {canEdit && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => onEdit(feedback)}
                                >
                                    <Pencil size={14} />
                                </Button>
                            )}
                            {canDelete && (
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => onDelete(feedback.id)}
                                >
                                    <Trash2 size={14} />
                                </Button>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

const Feedbacks = () => {
    const { canEdit, canDelete } = useUserData();
    const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingFeedback, setEditingFeedback] = useState<Feedback | null>(null);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState<FeedbackCreateRequest>({
        name: '',
        message: '',
        rating: 5,
        avatar_color: AVATAR_COLORS[0],
        order_position: 0,
    });

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        fetchFeedbacks();
    }, []);

    const fetchFeedbacks = async () => {
        try {
            setLoading(true);
            const data = await feedbackService.getAll();
            setFeedbacks(data);
        } catch (err: any) {
            showError('Error!', err.response?.data?.message || 'Failed to load feedbacks');
            console.error('Error loading feedbacks:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingFeedback(null);
        setFormData({
            name: '',
            message: '',
            rating: 5,
            avatar_color: AVATAR_COLORS[0],
            order_position: feedbacks.length + 1,
        });
        setShowModal(true);
    };

    const handleEdit = (feedback: Feedback) => {
        setEditingFeedback(feedback);
        setFormData({
            name: feedback.name,
            message: feedback.message,
            rating: feedback.rating,
            avatar_color: feedback.avatar_color,
            order_position: feedback.order_position,
        });
        setShowModal(true);
    };

    const handleDelete = async (id: number) => {
        const confirmed = await showConfirm(
            'Are you sure?',
            'This feedback will be permanently deleted!',
            'Yes, delete it!',
            'Cancel'
        );

        if (!confirmed) return;

        try {
            await feedbackService.delete(id);
            setFeedbacks(feedbacks.filter(f => f.id !== id));
            showSuccess('Deleted!', 'Feedback has been deleted successfully.');
        } catch (err: any) {
            showError('Error!', err.response?.data?.message || 'Failed to delete feedback');
            console.error('Error deleting feedback:', err);
        }
    };

    const validateForm = (): boolean => {
        if (!formData.name.trim()) {
            showError('Validation Error', 'Name is required');
            return false;
        }
        if (!formData.message.trim()) {
            showError('Validation Error', 'Message is required');
            return false;
        }
        if (formData.rating < 1 || formData.rating > 5) {
            showError('Validation Error', 'Rating must be between 1 and 5');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSaving(true);

            if (editingFeedback) {
                const updated = await feedbackService.update(editingFeedback.id, formData);
                setFeedbacks(feedbacks.map(f => (f.id === editingFeedback.id ? updated : f)));
                showSuccess('Updated!', 'Feedback has been updated successfully.');
            } else {
                const newFeedback = await feedbackService.create(formData);
                setFeedbacks([...feedbacks, newFeedback]);
                showSuccess('Created!', 'Feedback has been created successfully.');
            }

            setShowModal(false);
        } catch (err: any) {
            showError('Error!', err.response?.data?.message || 'Failed to save feedback');
            console.error('Error saving feedback:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleDragEnd = async (event: any) => {
        const { active, over } = event;

        if (!over || active.id === over.id) return;

        const oldIndex = feedbacks.findIndex(f => f.id === active.id);
        const newIndex = feedbacks.findIndex(f => f.id === over.id);

        const reorderedFeedbacks = arrayMove(feedbacks, oldIndex, newIndex).map((f, index) => ({
            ...f,
            order_position: index + 1,
        }));

        setFeedbacks(reorderedFeedbacks);

        try {
            await feedbackService.reorder(reorderedFeedbacks);
            showSuccess('Reordered!', 'Feedback order has been updated.');
        } catch (err: any) {
            showError('Error!', 'Failed to update feedback order');
            console.error('Error reordering feedbacks:', err);
            // Revert on error
            fetchFeedbacks();
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 d-none d-lg-block">Feedbacks Management</h4>
                {canEdit && (
                    <Button variant="primary" onClick={handleAdd}>
                        <Plus size={18} className="me-2" />
                        Add New Feedback
                    </Button>
                )}
            </div>

            {loading ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading feedbacks...</p>
                    </Card.Body>
                </Card>
            ) : feedbacks.length === 0 ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Star size={48} className="text-muted mb-3" />
                        <p className="text-muted">No feedbacks found</p>
                        {canEdit && (
                            <Button variant="primary" onClick={handleAdd}>
                                <Plus size={18} className="me-2" />
                                Add First Feedback
                            </Button>
                        )}
                    </Card.Body>
                </Card>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={feedbacks.map(f => f.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {feedbacks.map(feedback => (
                            <SortableFeedbackItem
                                key={feedback.id}
                                feedback={feedback}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                canEdit={canEdit}
                                canDelete={canDelete}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            )}

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingFeedback ? 'Edit Feedback' : 'Add New Feedback'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={e =>
                                    setFormData({ ...formData, name: e.target.value })
                                }
                                placeholder="Enter customer name"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Message <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                value={formData.message}
                                onChange={e =>
                                    setFormData({ ...formData, message: e.target.value })
                                }
                                placeholder="Enter feedback message"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Rating <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                                value={formData.rating}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        rating: parseInt(e.target.value),
                                    })
                                }
                                required
                            >
                                {[5, 4, 3, 2, 1].map(rating => (
                                    <option key={rating} value={rating}>
                                        {rating} Star{rating !== 1 ? 's' : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Avatar Color</Form.Label>
                            <div className="d-flex gap-2 flex-wrap">
                                {AVATAR_COLORS.map(color => (
                                    <div
                                        key={color}
                                        onClick={() =>
                                            setFormData({ ...formData, avatar_color: color })
                                        }
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            backgroundColor: color,
                                            cursor: 'pointer',
                                            border:
                                                formData.avatar_color === color
                                                    ? '3px solid #000'
                                                    : '2px solid #e5e7eb',
                                        }}
                                    />
                                ))}
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Order Position</Form.Label>
                            <Form.Control
                                type="number"
                                min="1"
                                value={formData.order_position}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        order_position: parseInt(e.target.value) || 0,
                                    })
                                }
                                placeholder="Enter display order"
                            />
                            <Form.Text className="text-muted">
                                Lower numbers appear first. You can also drag to reorder.
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowModal(false)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    {canEdit && (
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Saving...
                                </>
                            ) : editingFeedback ? (
                                'Update'
                            ) : (
                                'Create'
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Feedbacks;
